import { TZDate } from '@date-fns/tz'
import { addWeeks, subWeeks } from 'date-fns'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	CALENDAR_END_HOUR,
	CALENDAR_HOUR_LABELS,
	CALENDAR_START_HOUR,
	dateToMinutesFromMidnight,
	durationToPx,
	findSlotStartInRegion,
	formatDateInTimezone,
	formatHourLabel,
	formatTimeLabel,
	getRegionsForDate,
	getWeekStartingFrom,
	HOUR_HEIGHT_PX,
	minutesToPx,
	pxToMinutes,
} from './calendar-math'
import type { PlaceableRegion, SelectedSlot } from './types'

interface Props {
	regions: PlaceableRegion[]
	durationMinutes: number
	candidateTimezone: string
	selectedSlot: SelectedSlot | null
	onSlotSelect: (slot: SelectedSlot) => void
	initialAnchor: Date
}

const CALENDAR_HEIGHT = (CALENDAR_END_HOUR - CALENDAR_START_HOUR) * HOUR_HEIGHT_PX

function minutesOnDateToUtc(minutesFromMidnight: number, localDateStr: string, timezone: string): Date {
	const hours = Math.floor(minutesFromMidnight / 60)
	const minutes = minutesFromMidnight % 60
	const tzDate = new TZDate(
		parseInt(localDateStr.slice(0, 4)),
		parseInt(localDateStr.slice(5, 7)) - 1,
		parseInt(localDateStr.slice(8, 10)),
		hours,
		minutes,
		0,
		timezone,
	)
	return new Date(tzDate.getTime())
}

export function CalendarWeekView(
	{ regions, durationMinutes, candidateTimezone, selectedSlot, onSlotSelect, initialAnchor }: Props,
) {
	const { i18n } = useTranslation()
	const [weekAnchor, setWeekAnchor] = useState(initialAnchor)
	const weekDates = getWeekStartingFrom(weekAnchor)

	const [ghost, setGhost] = useState<{ dayIndex: number; startMinutes: number } | null>(null)
	const columnRefs = useRef<(HTMLDivElement | null)[]>([])

	function handleMouseMove(dayIndex: number, e: React.MouseEvent<HTMLDivElement>) {
		const col = columnRefs.current[dayIndex]
		if (!col) { return }
		const rect = col.getBoundingClientRect()
		const yPx = e.clientY - rect.top
		const cursorMinutes = pxToMinutes(yPx)

		if (cursorMinutes < CALENDAR_START_HOUR * 60 || cursorMinutes > CALENDAR_END_HOUR * 60) {
			setGhost(null)
			return
		}

		const dayRegions = getRegionsForDate(weekDates[dayIndex], regions, candidateTimezone)
		const slotStart = findSlotStartInRegion(cursorMinutes, durationMinutes, dayRegions, candidateTimezone)
		if (slotStart !== null) {
			setGhost({ dayIndex, startMinutes: slotStart })
		} else {
			setGhost(null)
		}
	}

	function handleClick(dayIndex: number) {
		if (ghost?.dayIndex !== dayIndex) { return }
		const localDateStr = formatDateInTimezone(weekDates[dayIndex], candidateTimezone)
		const startsAt = minutesOnDateToUtc(ghost.startMinutes, localDateStr, candidateTimezone)
		const endsAt = new Date(startsAt.getTime() + durationMinutes * 60_000)
		onSlotSelect({ startsAt, endsAt })
	}

	const todayStr = formatDateInTimezone(new Date(), candidateTimezone)

	return (
		<div className="flex flex-col gap-3">
			{/* Week navigation */}
			<div className="flex items-center justify-between">
				<button
					type="button"
					onClick={() => setWeekAnchor((a) => subWeeks(a, 1))}
					className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
				>
					‹
				</button>
				<span className="text-sm font-medium text-gray-600">
					{weekDates[0].toLocaleDateString(i18n.language, {
						month: 'short',
						day: 'numeric',
						timeZone: candidateTimezone,
					})}
					{' – '}
					{weekDates[6].toLocaleDateString(i18n.language, {
						month: 'short',
						day: 'numeric',
						timeZone: candidateTimezone,
					})}
				</span>
				<button
					type="button"
					onClick={() => setWeekAnchor((a) => addWeeks(a, 1))}
					className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
				>
					›
				</button>
			</div>

			{/* Calendar grid */}
			<div className="flex">
				{/* Hour labels */}
				<div className="mr-2 flex shrink-0 flex-col" style={{ width: 28, paddingTop: 32 }}>
					<div className="relative" style={{ height: CALENDAR_HEIGHT }}>
						{CALENDAR_HOUR_LABELS.map((hour) => (
							<div
								key={hour}
								className="absolute right-0 text-[10px] text-gray-300"
								style={{ top: minutesToPx(hour * 60) - 6 }}
							>
								{formatHourLabel(hour)}
							</div>
						))}
					</div>
				</div>

				{/* Day columns */}
				<div className="flex flex-1 gap-px">
					{weekDates.map((date, dayIndex) => {
						const localDateStr = formatDateInTimezone(date, candidateTimezone)
						const isToday = localDateStr === todayStr
						const dayRegions = getRegionsForDate(date, regions, candidateTimezone)
						const isGhostDay = ghost != null && ghost.dayIndex === dayIndex
						const selectedOnThisDay = selectedSlot
							? formatDateInTimezone(selectedSlot.startsAt, candidateTimezone) === localDateStr
							: false

						return (
							<div key={dayIndex} className="flex min-w-0 flex-1 flex-col">
								{/* Day header */}
								<div className="mb-1 text-center">
									<div
										className={`text-[10px] uppercase ${
											isToday ? 'font-bold text-gray-900' : 'text-gray-400'
										}`}
									>
										{date.toLocaleDateString(i18n.language, {
											weekday: 'short',
											timeZone: candidateTimezone,
										})}
									</div>
									<div
										className={`text-xs font-semibold ${
											isToday ? 'text-blue-600' : 'text-gray-600'
										}`}
									>
										{date.toLocaleDateString(i18n.language, {
											day: 'numeric',
											timeZone: candidateTimezone,
										})}
									</div>
								</div>

								{/* Column grid */}
								<div
									ref={(el) => {
										columnRefs.current[dayIndex] = el
									}}
									className="relative cursor-crosshair bg-gray-50"
									style={{ height: CALENDAR_HEIGHT }}
									onMouseMove={(e) => handleMouseMove(dayIndex, e)}
									onMouseLeave={() => setGhost(null)}
									onClick={() => handleClick(dayIndex)}
								>
									{/* Hour gridlines */}
									{Array.from({ length: CALENDAR_END_HOUR - CALENDAR_START_HOUR + 1 }, (_, i) => (
										<div
											key={i}
											className="absolute inset-x-0 border-t border-gray-100"
											style={{ top: i * HOUR_HEIGHT_PX }}
										/>
									))}

									{/* Available regions */}
									{dayRegions.map((region, regionIndex) => {
										const regionStartMinutes = dateToMinutesFromMidnight(
											region.placeableFrom,
											candidateTimezone,
										)
										const regionEndMinutes = dateToMinutesFromMidnight(
											region.placeableTo,
											candidateTimezone,
										)
										const top = minutesToPx(regionStartMinutes)
										const height = minutesToPx(regionEndMinutes) - top
										return (
											<div
												key={regionIndex}
												className="absolute inset-x-0 bg-blue-50"
												style={{ top, height }}
											/>
										)
									})}

									{/* Ghost box */}
									{isGhostDay && ghost != null
										? (
											<div
												className="pointer-events-none absolute inset-x-0.5 rounded bg-blue-400/40 ring-1 ring-blue-400"
												style={{
													top: minutesToPx(ghost.startMinutes),
													height: durationToPx(durationMinutes),
												}}
											>
												<span className="block px-1 pt-0.5 text-[10px] font-medium text-blue-700">
													{formatTimeLabel(ghost.startMinutes, candidateTimezone)}
												</span>
											</div>
										)
										: null}

									{/* Selected slot */}
									{Boolean(selectedOnThisDay) && selectedSlot != null && (() => {
										const startMinutes = dateToMinutesFromMidnight(
											selectedSlot.startsAt,
											candidateTimezone,
										)
										return (
											<div
												className="pointer-events-none absolute inset-x-0.5 rounded bg-blue-600 ring-1 ring-blue-700"
												style={{
													top: minutesToPx(startMinutes),
													height: durationToPx(durationMinutes),
												}}
											>
												<span className="block px-1 pt-0.5 text-[10px] font-medium text-white">
													{formatTimeLabel(startMinutes, candidateTimezone)}
												</span>
											</div>
										)
									})()}
								</div>
							</div>
						)
					})}
				</div>
			</div>
		</div>
	)
}
